/**
 * Native ReAct executor assembly.
 *
 * The production plan-protocol runner remains untouched. This module creates a
 * separate run-scoped runtime that reuses hardened tool implementations while
 * removing the model-authored planning controls Sid's native loop retired.
 */

import { MAX_EXECUTOR_ITERATIONS } from "./executorLimits";
import { withChatHistoryGuidance } from "./chatHistoryContext";
import {
  blockedOperationExactlyMatches,
  buildExecutorSkillAssembly,
  ingestResearchToolResult,
  granularRecoveryKey,
  isConfirmedWriteResult,
  isReplayGuardedWriteToolCall,
  isStatefulToolCall,
  narrowGranularRemediationCells,
  recoverAmbiguousGranularRangeWrite,
  resolveWorksheetColumnRoute,
  isDirectIdLookupSourceRestricted,
  shouldCheckpointBlockedWrite,
  stableWriteFingerprint,
  processApplyTemplateOutcomes,
  withApplyTemplateOutcomeCapture,
  writeOverlapsBlockedOperation,
  type ExecutorRunnerConfig,
  type ExecutorRunEvent,
} from "./executorRunner";
import {
  detectIdLookupRoutingIntent,
  detectIdLookupWorksheetWorkload,
  type IdLookupRoutingIntent,
} from "./idLookupRoutingPolicy";
import { resolveIdLookupAvailability } from "./idLookupMode";
import { getIdLookupToolInstruction } from "./idLookupTool";
import { getTabularQueryWorkloadInstruction } from "./tabularQueryWorkload";
import {
  detectWorksheetCompanyList,
  detectWorksheetCompanyListFromContext,
} from "./worksheetCompanyList";
import {
  createExecutorToolRuntime,
  type ExecutorRunAccumulators,
  type ExecutorRunSessionState,
  type ExecutorToolRuntimeContext,
  type RuntimeToolExecutor,
} from "./executorToolRuntime";
import { FactSetFormulaFormatRegistry } from "./factsetFormulaFormatRegistry";
import type { ChartCapabilityToolDef } from "./chartCapabilityTools";
import type {
  ExecutorPlanState,
  ExecutorRunCheckpoint,
  ExecutorRunResult,
  ExecutorUnifiedRunResult,
} from "./types";
import {
  COMPLETE_RUN_TOOL_NAME,
  runBoundedNativeReactHarness,
  type NativeReactHarnessConfig,
} from "./executorNativeReactHarness";
import { ExecutorWorkbookHandoffError } from "./executorWorkbookTools";
import { serializeNativeWorkbookBootstrap } from "./executorNativeWorkbookContext";
import { withCredentials } from "../shared/apiClient";
import { fetchWithRetry } from "../shared/utils/http";
import { isExcelClaudeOpusEnabled } from "../shared/featureFlags/excelExecutorModel";
import {
  FLAGGED_EXECUTOR_MODEL_ID,
  getExecutorAdapter,
  resolveExecutorModel,
  type AgentTurn,
  type AgentTurnResult,
} from "./providers";
import type { ExecutorModelConfig } from "./providers/registry";
import type { ExecutorProviderAdapter } from "./providers/types";
import { executeToolCallDetailed, type ToolCallDetailedResult } from "./toolRegistry";
import {
  buildExcelUnifiedAgentGenerationMetadata,
  withOptionalExcelLangfuseMetadata,
} from "./langfuseTrace";
import { postExcelToolObservation } from "./langfuseToolObservation";
import {
  isAnyResearchToolName,
  type FinsterStreamSubEvent,
} from "../shared/api/finster/queryTool";
import { getFinsterQueryToolInstruction } from "./finsterQueryTool";
import { extractCitedValuesById } from "./citationNotes";
import type { ExcelAgentSource } from "./agentTimeline";
import type { ApplyTemplateOutcome } from "./templateTools";
import { executeFactSetFormatSafetyPass } from "./executorWriteTools";

/** Stable internal step used by all native-mode write calls. */
export const NATIVE_RUN_STEP_ID = "native_run";

/** Legacy model-authored planning controls hidden only in native mode. */
export const NATIVE_REACT_HIDDEN_CONTROL_TOOLS = [
  "set_run_plan",
  "set_step_status",
  "revise_plan",
] as const;

const HIDDEN_CONTROL_TOOL_NAMES = new Set<string>(NATIVE_REACT_HIDDEN_CONTROL_TOOLS);

export const COMPLETE_RUN_TOOL: ChartCapabilityToolDef = {
  name: COMPLETE_RUN_TOOL_NAME,
  description:
    "End the native workflow. First write the final user-facing explanation as assistant text in the same turn, covering what you did, what worked, what did not, why, and what the user should examine next when relevant. Then call complete_run with no arguments and no other tool calls.",
  inputSchema: {
    type: "object",
    properties: {},
    required: [],
    additionalProperties: false,
  },
  strict: true,
};

/**
 * Legacy native operational instructions retained for compatibility and
 * focused safety-contract tests. The live native prompt is assembled by
 * {@link buildNativeReactSystemPrompt} and does not include this block.
 */
export const NATIVE_REACT_SYSTEM_PROMPT = `
You are the native ReAct execution agent for the Finster Excel add-in.
Reason from the user's request, inspect the live workbook with tools, act, and
verify the observed result before replying.

The initial workbook bootstrap is metadata only. It contains no trustworthy
cell values or formulas. Use list_sheets and targeted read_range calls for the
smallest ranges needed by the request. Do not read every sheet or an entire
large used range by default. If sheet inventory metadata was truncated, call
list_sheets before deciding scope. When a sheet has no usedRange metadata, its
used-range shape is unknown, not blank. Do not call read_range with a null address
or guess a broad range for that sheet; call list_sheets first.

There is one internal run step with id "${NATIVE_RUN_STEP_ID}". Pass that exact
stepId to every tool schema that requires one. The step exists only to retain
write validation and must not be described to the user as a plan.

Workbook changes must be grounded in tool observations. After a write, read the
changed range back before claiming completion. Never claim a host mutation that
the tool result did not confirm. Apply formatting only to the cells that require
it. Do not format entire rows or columns unless the user explicitly requests it.

Continue using tools while work remains. Assistant text communicates progress
and does not end the workflow. When the work is done, call complete_run alone
with the final user-facing answer and honest outcome. Do not expose tool names,
internal step ids, or hidden reasoning.
`.trim();

/**
 * Assemble native instructions from the banker persona, mandatory hygiene
 * documents, source scope, and chat-history safety policy. Optional capability
 * guidance belongs to the corresponding native tool definitions.
 *
 * The banker persona leads the prompt, followed by the complete mandatory
 * formatting, structure, data/formula, and QA hygiene documents. Specialist
 * modeling documents remain opt-in through `get_skill`.
 */
export function buildNativeReactSystemPrompt(
  config: ExecutorRunnerConfig,
  _routing: NativeToolRouting,
  now: Date = new Date(),
): string {
  const date = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(now);
  const bankerOperatingStandard = `

# Your Identity and Role
You are an intelligent investment banker and a specialist in Excel specific banker workflows.
You perform investment-banking knowledge work and produce thorough, world class client-ready output
which is complete, accurate, thorough and formatted to banker standard and expectations.
Bear that in mind in every query you answer and every decision you make.

# Guiding Principle
Before acting, read through the given brief, properly understand the semantics of the given workbook, the sheets, the formulae,
what each table or range is trying to do, just the way a banker would think and reason about it.
Decompose the given underlying ask based on its difficulty to make sense of the requested deliverable, just like a strong investment banker would do.
Then think through all the knowledge search, read, write and edit tools you have and how you would sequence them to solve the problem and complete
the user's request. When you have a clear understanding of the given workbook, the problem you need to solve for the user, and your approach to it,
make sure you communicate your thinking and approach to the user before proceeding on with executing your solution.
While working through the problem, at each and every decision point, your guiding principle should always be to ask yourself
what a strong investment banker would do: when understanding a given notebook, searching for information, figuring out a failure,
editing, building or formatting a workbook, following the style and
customizations for an org and user, deciding whether to edit something easily or build from scratch, cross-checking analyses, reviewing and deciding
whether it is client-ready.

# Goal - Non-negotiable
Your goal is to understand and deliver a user request to completion. You should not stop before
you have either completed the brief to the standard expected by the user or you are blocked and need
some specific information on something which you are unable to find yourself. Nothing else should or can
make you stop your work. At the end of every piece of work, you should deliver an explanation of what you did,
what is complete, what needs further looking into and why.
It is critical that you do not stop when hitting failures. Instead, rethink the approach, go back to the drawing
board, and investigate further when you have not properly solved the user’s problem. That is non-negotiable.

# Today's Date
Today is ${date}. Use this date when the request depends on "today", "latest",
or another relative period.

# Workbook intake & Problem Understanding
For a request that depends on the current workbook, first determine whether
the relevant live workbook evidence is present in the current context.
If it is not, complete the following intake *before* substantive external
research or workbook edits:
- inspect the workbook metadata and the relevant live ranges;
- establish the workbook purpose and extent, the actual semantics of the relevant sheets and ranges,
  the row or table grain, the meaning of important fields, the formulae and how they are related and connected;
- identify the requested deliverable and ambiguities, figure out the evidence already
  available locally, which will help you frame the evidence you would need external research for, and hence, the completion criteria;
- form a concise working semantic brief that connects those facts to the best
  complete approach for the user's request. Make sure you communicate this semantic brief to the user.

Read proportionally: use headers, representative rows, and targeted ranges to
understand structure, but inspect the complete relevant population when exact
coverage, filtering, ranking, grouping, or reconciliation depends on it. Do NOT
treat a small sample as evidence about the full population. Do NOT infer unseen
workbook content.

Make your analysis visible through a short progress update: state the concrete
workbook facts you established, what the user needs, and the approach and strategy you will
take to solve the problem. However, a purely conversational request or a task independent of workbook content
does not require workbook analysis. Always apply your best banker judgement to the query to act.

# Large Research Workloads
When a request involves many workbook rows, entities, periods, or requested facts, treat it as a
workload to design rather than just making a collection of independent research calls. Before substantive
external research, strategize how you can solve for a large workload.
Establish the complete relevant population, row grain, stable identifiers,
requested output, ranking or filtering criteria, and completion conditions.
Inspect the full relevant population when coverage matters; then filter, normalize, and deduplicate workbook
records while preserving source-row locations. This will help with your strategy to solve the problem.

Separate the work into facts already present or derivable from the workbook, broad screening or
structured factual enrichment, and deeper entity-specific qualitative research. Then think of the fastest, yet complete, 
reliable method for each category. Never delegate workbook extraction or deduplication
to a research agent that cannot see the workbook.

Design dependent stages deliberately—for example, screen → shortlist → investigate → rank → produce.
Reduce a large population before expensive qualitative work whenever the requested deliverable
allows it. Research only the entities and fields needed to satisfy the brief. Choose
research-query granularity dynamically based on the similarity and complexity of the requested
facts, expected response size, available sources, citation requirements, and observed result
quality. Avoid both an overloaded query that cannot provide complete entity-level coverage and
unnecessary one-call-per-row decomposition. Parallelize only genuinely independent work; sequence
stages when one result is needed to frame the next.

After each stage, assess coverage and result quality before proceeding. Reuse successful evidence
and issue materially different follow-ups only for specific unresolved gaps. Maintain a concise
coverage record of the original rows and distinct entities considered, filtering and
deduplication performed, entities screened or researched, fields completed or unresolved, and
the evidence and source-row mapping supporting the final output. Before finishing, validate that
the deliverable satisfies the original population, requested output, ranking logic, evidence
requirements, and workbook-formatting expectations.

Remember, you need to make your output complete and client ready and there is no room to stop before that is complete.
You are a world class investment banker. Apply your judgement accordingly to achieve the goal.

# Incremental Change and Review Loop
Work incrementally: inspect the relevant area, make the next bounded change, format it right
review it, and read back the result and iterate till the task is complete. Again, apply your banker judgement when
thinking here. In many cases, the user's ask may require editing or QA on a file instead of building something
from scratch. In such cases, make sure you preserve existing formulas, links, checks, conventions, and intentional hardcodes unless
the user asks to change them. Use finance-appropriate labels, units, signs,
precision, chronology, and formula conventions. Never trade completeness for a
quick partial answer.

When a tool fails, diagnose the returned evidence, modify the approach, and
retry with a different approach. Do not loop
on the same failed action if you can sense that it will fail again. The same thing can
be done in multiple ways. Again, apply best banker judgement on how you would tackle the problem.
Before stopping, verify calculations, formulae
continuity, requested coverage, formatting consistency, and that the finished
workbook and explanation are client-ready. If a material issue cannot be
resolved, state it precisely and explain why rather than claiming success. Also when part of the work
requires further user help and input, explain which bits and why they need to be looked at rather than claiming failure.

# Modeling skills
You have access to a range of skills for specific modeling tasks which contain more
detailed guidance on those tasks. Use those skills when you can sense that your task needs specialist modeling guidance.
Select the relevant set of skills that you would need to fulfill the deliverable to the expected standard;
the tool expands required dependencies automatically.

# Banker-grade formatting and presentation
Treat formatting as part of the deliverable, not optional decoration. Every workbook you create or modify must be
presentation-ready and meet professional investment-banking standards. Follow the applicable user, organization, and workbook-template style first;
and general formatting and layout skills should be available to you as the house standard. Before completing, review the edited ranges as a banker would: ensure consistent fonts, colours, number formats, formulae formats, units, dates, headers,
spacing, alignment, column widths, input/formula/source conventions, totals, footnotes, and source lines. Preserve the existing workbook’s visual
language when editing an established model. Correct formatting inconsistencies introduced by your work, and do not complete till the output is clear,
consistent, and suitable for review by a senior banker or client.

# Research tool calls
FactSet AI / Finster research tools cannot see or open the current Excel workbook.
They are stateless delegated agents and receive no workbook cells, formulas,
the conversation, prior calls, or hidden reasoning. Never ask them to inspect,
extract, filter, summarize, or cite the open workbook. First use Excel tools to
read the workbook facts needed to frame the question, then include those facts
explicitly in each self-contained research query. Make every research query
self-contained with the company/entity, periods, metrics, units, relevant
workbook facts and assumptions, and exact requested deliverable and format you would like to get the answer.
Never refer only to "this workbook", "the selected cells", or "the context above".
Decompose an overly broad research request into bounded, coherent subqueries by
company, topic, period, source, or analytical question when decomposition is the best strategy.
Ask for the answer in a clear format suited to the deliverable. Run independent
subqueries in parallel when their answers do not depend on one another;
sequence genuinely dependent subqueries when one result is needed to frame the next.
Every subquery must repeat the context it needs rather than relying on another
research call's hidden state.
Use research tools only in research-only turns. You may batch independent research
calls together, but do not combine a research call with any workbook, skill, or
other non-research tool call in the same turn.

# Completion protocol
You should communicate concise progress while continuing to reason and use tools.
You should expose your reasoning and thinking process to the user. Once your task is complete,
make sure you explain the analysis you did, what is complete, what still needs further input or looking into
from the user and why and the evidence behind that. To stop the loop, you need to call the \`complete_run\` tool
with no arguments and no other tool calls. That will stop the loop. Make sure you have everything in place and have
sent through all the workbook/worksheet changes and explanations to the user before calling this tool.
Again, you should NOT stop your working loop when facing some failures. Read the response, figure
out why and keep trying till your job is done. An incomplete job is not acceptable. Once the task is done, end the
workflow only by calling \`complete_run\`, and call it alone (as the only tool) in its turn.
`.trim();
  const mandatoryHygieneGuidance = buildExecutorSkillAssembly(
    undefined,
    config.brandId,
  ).skillBlock;
  const researchGuidance = getFinsterQueryToolInstruction(
    undefined,
    config.toolCallContext?.researchContext,
  );
  const selectedFileCount = new Set(
    (config.toolCallContext?.researchContext?.manualUserFileIds ??
      config.toolCallContext?.researchContext?.userFileIds ??
      [])
      .map((fileId) => fileId.trim())
      .filter(Boolean),
  ).size;
  const selectedFileGuidance =
    selectedFileCount > 0
      ? `
# Selected-file evidence
The user explicitly selected ${selectedFileCount} ${selectedFileCount === 1 ? "file" : "files"} for this request. Treat the selected files as required evidence in your assessment of what to do.

First inspect enough workbook context to frame a self-contained research query,
then use the full FactSet AI / Finster research tool with the immutable selected-file
scope. Incorporate the retrieved file evidence into your reasoning and any
workbook changes. If retrieval fails or the files contain no relevant evidence,
say so explicitly and continue only with work that does not depend on them.
Do not claim that you considered the selected files unless retrieval succeeded.
`.trim()
      : "";

  return withChatHistoryGuidance(
    [
      bankerOperatingStandard,
      mandatoryHygieneGuidance,
      selectedFileGuidance,
      researchGuidance,
    ].filter(Boolean).join("\n\n"),
  );
}

/**
 * Attach request-scoped capability guidance to the native tool definitions
 * that need it, rather than expanding the native system prompt.
 *
 * Provider serializers currently expose `name` and `description` at the top
 * level for both OpenAI and Anthropic, so this transformation remains
 * provider-neutral. Every definition is cloned; shared registry definitions
 * used by the legacy harness are never mutated.
 */
export function applyNativeToolLocalGuidance(
  tools: readonly unknown[],
  routing: NativeToolRouting,
): unknown[] {
  const directLookupGuidance = routing.includeIdLookup
    ? getIdLookupToolInstruction({
        includeDirectLookup: true,
        includeWorksheetWorkload: false,
      })
    : "";
  const worksheetLookupGuidance = routing.includeIdLookupWorkload
    ? getIdLookupToolInstruction({
        includeDirectLookup: false,
        includeWorksheetWorkload: true,
      })
    : "";
  const tabularGuidance = routing.includeTabularQueryWorkload
    ? getTabularQueryWorkloadInstruction()
    : "";
  const researchRoutingGuidance = routing.includeIdLookupWorkload
    ? "Do not use this research tool for that routed worksheet workload; use the registered ID-lookup workload lifecycle."
    : routing.includeTabularQueryWorkload
      ? "Do not use this research tool for that routed worksheet workload; use the registered tabular workload lifecycle."
      : routing.includeIdLookup
        ? "For a routed single-company identity request, use id_lookup first and use research only for a specifically requested field that lookup could not return."
        : "";

  return tools.map((tool) => {
    if (!tool || typeof tool !== "object" || Array.isArray(tool)) return tool;
    const definition = tool as Record<string, unknown>;
    const name = typeof definition.name === "string" ? definition.name : "";
    const baseDescription =
      typeof definition.description === "string" ? definition.description : "";
    let guidance = "";
    if (name === "id_lookup") guidance = directLookupGuidance;
    if (name === "start_id_lookup_workload") guidance = worksheetLookupGuidance;
    if (name === "start_tabular_query_workload") guidance = tabularGuidance;
    if (isAnyResearchToolName(name)) guidance = researchRoutingGuidance;
    if (!guidance) return { ...definition };
    return {
      ...definition,
      description: `${baseDescription}\n\n${guidance.trim()}`.trim(),
    };
  });
}

/** Fully assembled state and tool surface for one native ReAct run. */
export interface NativeReactRuntime {
  /** Internal synthetic plan state used by existing write safety checks. */
  planState: ExecutorPlanState;
  /** Shared mutation/result accumulators consumed by finalization. */
  accumulators: ExecutorRunAccumulators;
  /** Workbook target and run-scoped safety state. */
  session: ExecutorRunSessionState;
  /** Runtime context retained for diagnostics and focused tests. */
  context: ExecutorToolRuntimeContext;
  /** Provider-visible native tool definitions. */
  toolDefs: readonly ChartCapabilityToolDef[];
  /** Native dispatch map with plan-control handlers removed. */
  executors: Record<string, RuntimeToolExecutor>;
  /** Research source cards accumulated without replaying citations to the model. */
  retrievedSources: ExcelAgentSource[];
  /** Raw research citations accumulated for UI and persistence parity. */
  retrievedCitations: unknown[];
}

/** Non-model-facing metadata retained until one tool completion event is emitted. */
interface NativeToolCompletionMetadata {
  elapsedMs: number;
  researchCitations?: unknown[];
  researchCitedValuesByUuid?: Record<string, string>;
  observationMetadata?: Record<string, unknown>;
}

/** Injectable seams for focused native-runner tests. */
export interface NativeReactRunnerDependencies {
  /** Bypasses provider transport while retaining the real harness/runtime. */
  requestTurn?: NativeReactHarnessConfig["requestTurn"];
  /** Optional provider adapter override. */
  adapter?: ExecutorProviderAdapter;
  /** Optional resolved model override. */
  model?: ExecutorModelConfig;
  /** Optional hard model-turn cap. */
  maxIterations?: number;
  /** Optional hard wall-clock budget. */
  maxDurationMs?: number;
  /** Optional unchanged-repeat budget. */
  maxStagnantIterations?: number;
  /** Optional clock for deterministic timeout tests. */
  now?: () => number;
}

/** Request-scoped tool authorization resolved before native runtime assembly. */
export interface NativeToolRouting {
  /** Expose the direct single-entity ID lookup tool. */
  includeIdLookup: boolean;
  /** Expose the guarded worksheet ID-lookup workload tools. */
  includeIdLookupWorkload: boolean;
  /** Expose the guarded worksheet tabular-query workload tools. */
  includeTabularQueryWorkload: boolean;
  /** Authorized identity fields inferred from the user request. */
  idLookupRoutingIntent: IdLookupRoutingIntent;
}

/**
 * Build the native runtime beside the unchanged production plan runner.
 *
 * @param config - Existing runner dependencies and run-scoped workbook target.
 * @param routing - Optional request-scoped direct/workload tool authorization.
 * @returns Native-only definitions, dispatchers, and synthetic safety state.
 */
export function createNativeReactRuntime(
  config: ExecutorRunnerConfig,
  routing?: NativeToolRouting
): NativeReactRuntime {
  const planState: ExecutorPlanState = {
    summary: "Native ReAct run",
    estimatedComplexity: "moderate",
    currentStepId: NATIVE_RUN_STEP_ID,
    steps: [
      {
        id: NATIVE_RUN_STEP_ID,
        status: "running",
        planStep: {
          step: 1,
          description: config.userRequest,
          action: "native_react",
          ...(config.sessionLockSheet ? { targetSheet: config.sessionLockSheet } : {}),
        },
      },
    ],
  };
  const accumulators = createNativeAccumulators(config.userRequest);
  const session: ExecutorRunSessionState = {
    sessionLockSheet: config.sessionLockSheet,
    userRequest: config.userRequest,
    pinnedWorkbookName: config.pinnedWorkbookName,
    citationNoteCapability:
      config.bridgeAvailable === false ? "unsupported" : config.citationNoteCapability,
    citationNoteAttachmentDisabled:
      config.bridgeAvailable === false || config.citationNoteCapability === "unsupported",
  };
  const context: ExecutorToolRuntimeContext = {
    planState,
    onPlanStateChange: config.onPlanStateChange,
    applyWorkbookModifications: config.applyWorkbookModifications,
    accumulators,
    session,
    onWorkbookHandoff: config.onWorkbookHandoff,
    beginWorkbookHandoff: config.beginWorkbookHandoff,
    idLookupRequestedFields: routing?.idLookupRoutingIntent.requestedFields,
    idLookupWorkloadEnabled: routing?.includeIdLookupWorkload === true,
    excelTabularQuery:
      routing?.includeTabularQueryWorkload && config.excelTabularQuery
        ? { ...config.excelTabularQuery, enabled: true }
        : undefined,
    tabularQueryClient: config.tabularQueryClient,
    tabularIdentityResolver: config.tabularIdentityResolver,
    researchContext: config.toolCallContext?.researchContext,
    userSkillGuidance: config.toolCallContext?.userSkillGuidance,
    langfuseContext: config.langfuseContext,
    signal: config.signal,
    getRemainingExecutorIterations: () => MAX_EXECUTOR_ITERATIONS,
    factSetFormatRegistry: new FactSetFormulaFormatRegistry(),
    refreshFactSetData: config.refreshFactSetData,
    recalculateWorkbook: config.recalculateWorkbook,
  };
  const shared = createExecutorToolRuntime(context);
  const toolDefs: ChartCapabilityToolDef[] = [
    ...shared.toolDefs.filter((tool) => !HIDDEN_CONTROL_TOOL_NAMES.has(tool.name)),
    COMPLETE_RUN_TOOL,
  ];
  const executors = Object.fromEntries(
    Object.entries(shared.executors).filter(([name]) => !HIDDEN_CONTROL_TOOL_NAMES.has(name))
  ) as Record<string, RuntimeToolExecutor>;

  return {
    planState,
    accumulators,
    session,
    context,
    toolDefs,
    executors,
    retrievedSources: [],
    retrievedCitations: [],
  };
}

/**
 * Apply the same request-scoped ID-lookup and tabular routing policy as the
 * current plan runner before exposing native tools.
 *
 * @param config - Run configuration, user request, workbook metadata, and gates.
 * @returns The authorized direct and worksheet-workload tool selections.
 */
async function resolveNativeToolRouting(
  config: ExecutorRunnerConfig
): Promise<NativeToolRouting> {
  const detectedFromText = detectIdLookupRoutingIntent(
    config.userRequest,
    config.userRequest
  );
  const companyList = config.workbookSnapshot
    ? detectWorksheetCompanyList(config.workbookSnapshot)
    : detectWorksheetCompanyListFromContext(config.workbookContext);
  const worksheetRoute = await resolveWorksheetColumnRoute({
    userRequest: config.userRequest,
    tabularEnabled: config.excelTabularQuery?.enabled === true,
    companyListPresent: companyList.present,
    idLookupIntent: detectedFromText,
    signal: config.signal,
  });
  const detected = worksheetRoute.idLookupIntent;
  const isWorksheetListLookup =
    detected.active &&
    (detectIdLookupWorksheetWorkload(config.userRequest) ||
      worksheetRoute.inferredWorksheetList === true);
  const sourceRestricted = isDirectIdLookupSourceRestricted(
    config.toolCallContext?.researchContext
  );
  const directEligible =
    detected.active && !isWorksheetListLookup && !sourceRestricted;
  const workloadEligible =
    detected.active &&
    isWorksheetListLookup &&
    !sourceRestricted &&
    worksheetRoute.route !== "tabular";
  const availability =
    directEligible || workloadEligible
      ? await resolveIdLookupAvailability({
          log: (message) => config.onEvent?.({ type: "status", message }),
        })
      : { available: false as const };
  const includeIdLookup = directEligible && availability.available;
  const includeIdLookupWorkload =
    workloadEligible && availability.available;
  const includeTabularQueryWorkload =
    worksheetRoute.route === "tabular" &&
    config.excelTabularQuery?.enabled === true;
  return {
    includeIdLookup,
    includeIdLookupWorkload,
    includeTabularQueryWorkload,
    idLookupRoutingIntent:
      includeIdLookup || includeIdLookupWorkload
        ? detected
        : { active: false, requestedFields: [] },
  };
}

/**
 * Build the native user envelope with metadata-only workbook bootstrap.
 *
 * @param config - Existing run configuration and workbook snapshot.
 * @returns Serialized user request safe for the first native model turn.
 */
export function buildNativeReactUserMessage(config: ExecutorRunnerConfig): string {
  return JSON.stringify({
    userRequest: config.userRequest,
    ...(config.chatHistory?.trim() ? { chatHistory: config.chatHistory.trim() } : {}),
    internalStepId: NATIVE_RUN_STEP_ID,
    workbook: JSON.parse(
      serializeNativeWorkbookBootstrap({
        workbookSnapshot: config.workbookSnapshot,
        workbookContext: config.workbookContext,
      })
    ) as unknown,
    responseFormat:
      "Use tools until work is complete, write the final user-facing explanation as assistant text, then call complete_run with no arguments and no other tool calls.",
  });
}

/**
 * Execute one native ReAct run beside the production plan runner.
 *
 * The feature gate and UI dispatcher are intentionally outside this function;
 * direct tests can exercise the dark path while production remains unchanged.
 *
 * @param config - Existing executor host, context, and observability callbacks.
 * @param dependencies - Optional provider and budget seams for focused tests.
 * @returns Existing unified result shape consumed by Excel finalization.
 */
export async function runNativeReactExecutor(
  config: ExecutorRunnerConfig,
  dependencies: NativeReactRunnerDependencies = {}
): Promise<ExecutorUnifiedRunResult> {
  const routing = await resolveNativeToolRouting(config);
  const runtime = createNativeReactRuntime(config, routing);
  const model =
    dependencies.model ??
    resolveExecutorModel(isExcelClaudeOpusEnabled() ? FLAGGED_EXECUTOR_MODEL_ID : null);
  const adapter = dependencies.adapter ?? getExecutorAdapter(model.provider);
  const tools = applyNativeToolLocalGuidance(
    adapter.serializeTools({
      scope: "react-executor",
      extraTools: runtime.toolDefs,
      options: { includeIdLookup: routing.includeIdLookup },
    }),
    routing,
  );
  const instructions = buildNativeReactSystemPrompt(config, routing);
  const initialTurns: AgentTurn[] = [
    ...(config.preludeUserMessages ?? []).map((content): AgentTurn => ({ kind: "user", content })),
    { kind: "user", content: buildNativeReactUserMessage(config) },
  ];
  const requestTurn =
    dependencies.requestTurn ??
    createProviderTurnRequester(config, adapter, model, tools, instructions);
  const completionMetadataByCallId = new Map<string, NativeToolCompletionMetadata>();
  const executedWriteFingerprints = new Set<string>();
  const granularRecoveryKeys = new Set<string>();
  let blockedOperation: ExecutorRunCheckpoint["blockedOperation"];
  const templateOutcomes: ApplyTemplateOutcome[] = [];
  const nativeToolCallContext = withApplyTemplateOutcomeCapture(
    config.toolCallContext,
    templateOutcomes
  );

  const harnessResult = await runBoundedNativeReactHarness({
    initialTurns,
    requestTurn,
    executeTool: async (call, execution) => {
      const args = parseToolArguments(call.arguments);
      const startedAtMs = Date.now();
      const templateOutcomeStart = templateOutcomes.length;
      let result: ToolCallDetailedResult;
      const writeFingerprint = isReplayGuardedWriteToolCall(call.name, args)
        ? stableWriteFingerprint(call.name, args)
        : undefined;
      const recoveryKey = granularRecoveryKey(call.name, args);
      const remediationCells =
        blockedOperation && isReplayGuardedWriteToolCall(call.name, args)
          ? narrowGranularRemediationCells(blockedOperation, call.name, args)
          : undefined;
      if (
        blockedOperation &&
        blockedOperationExactlyMatches(blockedOperation, call.name, args)
      ) {
        result = {
          output: JSON.stringify({
            ok: false,
            applied: false,
            replayPrevented: true,
            code: "partial_write_replay_blocked",
            editedCells: blockedOperation.confirmedEditedCells,
            error: "This exact write may have partially applied and was not replayed.",
            instruction:
              "Read back the original targets, then issue a narrower exact-shaped write only for coordinates still missing.",
          }),
        };
      } else if (
        blockedOperation &&
        !remediationCells &&
        isReplayGuardedWriteToolCall(call.name, args) &&
        writeOverlapsBlockedOperation(blockedOperation, call.name, args)
      ) {
        result = {
          output: JSON.stringify({
            ok: false,
            applied: false,
            replayPrevented: true,
            code: "blocked_write_requires_resolution",
            error: "This write overlaps cells whose prior host acknowledgement is unresolved.",
            instruction:
              "Resolve the blocked target by exact read-back and narrower exact-shaped writes before writing these cells again.",
          }),
        };
      } else if (recoveryKey && granularRecoveryKeys.has(recoveryKey)) {
        result = {
          output: JSON.stringify({
            ok: false,
            applied: false,
            recoveryExhausted: true,
            retryable: false,
            code: "range_write_recovery_exhausted",
            error:
              "The one readback-first recovery round for this exact range write is already exhausted.",
            instruction:
              "Do not replay the original write. Read the target and issue only narrower exact-shaped writes for proven missing coordinates.",
          }),
        };
      } else if (writeFingerprint && executedWriteFingerprints.has(writeFingerprint)) {
        result = {
          output: JSON.stringify({
            ok: true,
            applied: true,
            replayPrevented: true,
            writeFingerprint,
            message: "This exact workbook write was already confirmed and was not replayed.",
          }),
        };
      } else {
        try {
          const runtimeExecutor = runtime.executors[call.name];
          if (runtimeExecutor) {
            const runtimeResult = await runtimeExecutor(args, execution.signal, {
              callId: call.callId,
              iteration: execution.iteration,
            });
            result = typeof runtimeResult === "string" ? { output: runtimeResult } : runtimeResult;
          } else {
            const toolCallContext = isAnyResearchToolName(call.name)
              ? {
                  ...nativeToolCallContext,
                  researchExecutionMode: "stream" as const,
                  onResearchSubEvent: (event: FinsterStreamSubEvent) => {
                    try {
                      config.onEvent?.({
                        type: "research_sub_event",
                        callId: call.callId,
                        event,
                      });
                    } catch {
                      // Display-only streaming must never change research execution.
                    }
                  },
                }
              : nativeToolCallContext;
            result = await executeToolCallDetailed(
              call.name,
              args,
              execution.signal,
              toolCallContext
            );
          }
        } catch (error) {
          if (
            !writeFingerprint ||
            error instanceof ExecutorWorkbookHandoffError ||
            execution.signal?.aborted
          ) {
            throw error;
          }
          result = {
            output: JSON.stringify({
              ok: false,
              applied: false,
              ambiguousWrite: true,
              failedActions: ["tool dispatch acknowledgement"],
              error: error instanceof Error ? error.message : String(error),
            }),
          };
        }
        result = await recoverAmbiguousGranularRangeWrite({
          toolName: call.name,
          parsedArgs: args,
          result,
          executors: runtime.executors,
          signal: execution.signal,
          pinnedWorkbookName: runtime.session.pinnedWorkbookName,
          recoveryKeys: granularRecoveryKeys,
          accumulators: runtime.accumulators,
          onEvent: config.onEvent,
        });
        const parsedOutput = safeParseToolArguments(result.output);
        if (
          writeFingerprint &&
          parsedOutput &&
          typeof parsedOutput === "object" &&
          !Array.isArray(parsedOutput) &&
          isConfirmedWriteResult(parsedOutput as Record<string, unknown>)
        ) {
          executedWriteFingerprints.add(writeFingerprint);
          if (remediationCells && blockedOperation) {
            const remediated = new Set(
              remediationCells.map((address) => address.trim().toLowerCase())
            );
            const remaining = (blockedOperation.unresolvedCells ?? []).filter(
              (address) => !remediated.has(address.trim().toLowerCase())
            );
            blockedOperation =
              remaining.length > 0
                ? {
                    ...blockedOperation,
                    unresolvedCells: remaining,
                  }
                : undefined;
          }
        } else if (
          writeFingerprint &&
          parsedOutput &&
          typeof parsedOutput === "object" &&
          !Array.isArray(parsedOutput) &&
          shouldCheckpointBlockedWrite(parsedOutput as Record<string, unknown>)
        ) {
          const parsedRecord = parsedOutput as Record<string, unknown>;
          const unresolvedCells = Array.isArray(parsedRecord.recoveryAttempts)
            ? parsedRecord.recoveryAttempts.flatMap((attempt) => {
                if (!attempt || typeof attempt !== "object" || Array.isArray(attempt)) {
                  return [];
                }
                const candidate = attempt as {
                  address?: unknown;
                  applied?: unknown;
                };
                return candidate.applied !== true && typeof candidate.address === "string"
                  ? [candidate.address]
                  : [];
              })
            : [];
          blockedOperation = {
            toolName: call.name,
            args,
            reason:
              typeof parsedRecord.error === "string"
                ? parsedRecord.error
                : "Workbook write acknowledgement is unresolved.",
            confirmedEditedCells: Array.isArray(parsedRecord.editedCells)
              ? parsedRecord.editedCells.filter(
                  (value): value is string => typeof value === "string"
                )
              : undefined,
            readbackObserved: unresolvedCells.length > 0 ? true : undefined,
            unresolvedCells: unresolvedCells.length > 0 ? unresolvedCells : undefined,
          };
        }
      }
      const newTemplateOutcomes = templateOutcomes.slice(templateOutcomeStart);
      if (
        call.name === "apply_template" &&
        newTemplateOutcomes.some((outcome) => outcome.success)
      ) {
        runtime.accumulators.workbookEditAttempted = true;
        const processed = await processApplyTemplateOutcomes(
          newTemplateOutcomes,
          runtime.session,
          {
            probeActiveContext: config.probeActiveContext,
            bridgeAvailable: config.bridgeAvailable !== false,
            onWorkbookHandoff: config.onWorkbookHandoff,
          },
          config.onEvent
        );
        if (processed.shouldStop) {
          throw new ExecutorWorkbookHandoffError(
            "apply_template",
            runtime.session.pinnedWorkbookName ?? "template workbook",
            new Error(processed.reason)
          );
        }
      }
      if (call.name === "generate_formula") {
        runtime.context.factSetFormatRegistry?.recordGenerateFormulaOutput(
          safeParseToolArguments(result.output)
        );
      }
      const completionMetadata = buildNativeToolCompletionMetadata({
        toolName: call.name,
        result,
        elapsedMs: Math.max(0, Date.now() - startedAtMs),
      });
      completionMetadataByCallId.set(call.callId, completionMetadata);
      if (isAnyResearchToolName(call.name) && inferToolSuccess(result.output)) {
        ingestResearchToolResult(
          result.output,
          runtime.retrievedSources,
          runtime.retrievedCitations,
          runtime.accumulators,
          result.researchCitations
        );
      }
      postExcelToolObservation({
        context: config.langfuseContext,
        phase: "excel_unified_agent",
        executorMode: "native-react",
        toolName: call.name,
        args,
        result,
        startedAtMs,
      });
      return result.output;
    },
    isStatefulToolCall: (call) =>
      isStatefulToolCall(call.name, safeParseToolArguments(call.arguments)),
    isResearchToolCall: (call) => isAnyResearchToolName(call.name),
    progressSignature: () =>
      [
        runtime.accumulators.editedCells.length,
        runtime.accumulators.stepResults.length,
        runtime.accumulators.workbookEditAttempted,
      ].join("|"),
    maxIterations: dependencies.maxIterations,
    maxDurationMs: dependencies.maxDurationMs,
    maxStagnantIterations: dependencies.maxStagnantIterations,
    now: dependencies.now,
    signal: config.signal,
    isTerminalToolError: (error) => error instanceof ExecutorWorkbookHandoffError,
    onEvent: (event) => {
      forwardHarnessEvent(event, config.onEvent, completionMetadataByCallId);
    },
  });

  const attemptedEdit =
    runtime.accumulators.workbookEditAttempted || runtime.accumulators.editedCells.length > 0;
  if (attemptedEdit && !config.signal?.aborted) {
    await executeFactSetFormatSafetyPass(runtime.context);
  }
  if (!attemptedEdit) {
    return {
      mode: harnessResult.status === "completed" ? "answer" : "failed",
      reply: harnessResult.reply,
      workbookEditAttempted: false,
      retrievedSources: dedupeNativeSources(runtime.retrievedSources),
      retrievedCitations: runtime.retrievedCitations,
      retrievedCitedValuesByUuid: runtime.accumulators.retrievedCitedValuesByUuid ?? {},
    };
  }

  return buildNativeWorkbookResult(runtime, harnessResult);
}

/**
 * Initialize the mutable result collectors expected by the hardened shared
 * executor-tool runtime.
 *
 * Native mode owns fresh collectors per run to prevent workbook edits,
 * citations, sizing hints, or refresh debt from leaking across conversations.
 *
 * @param userRequest - Original user request shown in run diagnostics.
 * @returns Empty run-scoped accumulators compatible with shared tool handlers.
 */
function createNativeAccumulators(userRequest: string): ExecutorRunAccumulators {
  return {
    stepResults: [],
    stepResultIndexById: new Map(),
    stepDisplayResults: [userRequest],
    stepStatuses: ["queued"],
    lastModifications: null,
    editedCells: [],
    formulaWriteTargets: [],
    fdsFormulaWriteTargets: [],
    fdsFormulaWriteAttempts: {},
    unavailableFdsFormulaTargets: [],
    fdsDateAxisWriteTargets: [],
    autofitSheetNames: new Set(),
    autofitTargets: [],
    columnSizingHints: [],
    columnSizingCellEvidence: {},
    explicitColumnWidthKeys: [],
    fdsRefreshSheets: new Set(),
    deferredRefreshAllRequested: false,
    deferredRecalcMode: null,
    workbookEditAttempted: false,
    citationNoteIndex: {},
    citationCellProvenance: {},
    sourceNoteAudits: [],
    retrievedCitedValuesByUuid: {},
  };
}

/**
 * Adapt provider-neutral harness turns into one provider request callback.
 *
 * The returned callback preserves existing model selection, credentials,
 * retries, streaming reasoning, and Langfuse generation metadata while asking
 * only native mode for a plain-text terminal response.
 *
 * @param config - Existing executor transport and observability dependencies.
 * @param adapter - Provider implementation selected for this run.
 * @param model - Immutable model configuration selected at run start.
 * @param tools - Provider-serialized native tool surface.
 * @returns A harness-compatible function that performs one model turn.
 */
function createProviderTurnRequester(
  config: ExecutorRunnerConfig,
  adapter: ExecutorProviderAdapter,
  model: ExecutorModelConfig,
  tools: readonly unknown[],
  instructions: string,
): NativeReactHarnessConfig["requestTurn"] {
  return async ({ turns, iteration, signal }): Promise<AgentTurnResult> => {
    const encoded = adapter.encodeTurns(turns);
    const request = adapter.buildRequest({
      model,
      instructions,
      items: adapter.projectItems(encoded),
      tools,
      forcedToolName: null,
      terminalTextFormat: "plain_text",
    });
    let streamedReasoning = "";
    let streamedAssistantText = "";
    return adapter.streamTurn({
      request: () =>
        fetchWithRetry(
          request.url,
          config.withSignal(
            withOptionalExcelLangfuseMetadata(
              withCredentials({
                method: "POST",
                body: JSON.stringify(request.body),
                ...(request.headers ? { headers: request.headers } : {}),
              }),
              config.langfuseContext,
              buildExcelUnifiedAgentGenerationMetadata({
                executorMode: "native-react",
                provider: model.provider,
                model: model.model,
                iteration,
                stream: true,
                toolCount: tools.length,
              })
            )
          ),
          2,
          "native-react-executor"
        ),
      contextLabel: `native-react-executor ${model.label}`,
      signal,
      maxBodyRetries: 1,
      onText: (partial) => {
        const delta = partial.startsWith(streamedAssistantText)
          ? partial.slice(streamedAssistantText.length)
          : partial;
        streamedAssistantText = partial;
        if (!delta) return;
        config.onEvent?.({
          type: "assistant_text_delta",
          iteration,
          text: delta,
        });
      },
       onReasoning: (partial) => {
        const delta = partial.startsWith(streamedReasoning)
          ? partial.slice(streamedReasoning.length)
          : partial;
        streamedReasoning = partial;
        if (!delta) return;
        config.onReasoningDelta?.(delta, iteration);
        config.onEvent?.({
          type: "reasoning_delta",
          iteration,
          text: delta,
        });
      },
      onRetry: () => {
        streamedAssistantText = "";
        config.onEvent?.({
          type: "assistant_text_reset",
          iteration,
        });
        config.onEvent?.({
          type: "status",
          message: `Model stream interrupted; retrying native iteration ${iteration}.`,
        });
      },
    });
  };
}

/**
 * Translate a native mutation run into the existing workbook-result contract.
 *
 * @param runtime - Run-scoped tools, session, and mutation accumulators.
 * @param harnessResult - Terminal bounded-loop result.
 * @returns Existing Excel workbook result consumed by taskpane finalization.
 */
function buildNativeWorkbookResult(
  runtime: NativeReactRuntime,
  harnessResult: Awaited<ReturnType<typeof runBoundedNativeReactHarness>>
): ExecutorRunResult {
  return {
    mode:
      harnessResult.status === "completed"
        ? "workbook_edit"
        : harnessResult.status === "failed"
          ? "failed"
          : "partial",
    reply: harnessResult.reply,
    retrievedSources: dedupeNativeSources(runtime.retrievedSources),
    retrievedCitations: runtime.retrievedCitations,
    retrievedCitedValuesByUuid: runtime.accumulators.retrievedCitedValuesByUuid ?? {},
    plan: {
      summary: "",
      estimatedComplexity: "moderate",
      steps: [],
    },
    stepResults: [],
    stepDisplayResults: [],
    stepStatuses: [],
    lastModifications: runtime.accumulators.lastModifications,
    editedCells: runtime.accumulators.editedCells,
    formulaWriteTargets: runtime.accumulators.formulaWriteTargets,
    autofitSheetNames: runtime.accumulators.autofitSheetNames,
    autofitTargets: runtime.accumulators.autofitTargets,
    columnSizingHints: runtime.accumulators.columnSizingHints,
    explicitColumnWidthKeys: runtime.accumulators.explicitColumnWidthKeys,
    fdsRefreshSheets: runtime.accumulators.fdsRefreshSheets,
    deferredRefreshAllRequested: runtime.accumulators.deferredRefreshAllRequested,
    deferredRecalcMode: runtime.accumulators.deferredRecalcMode,
    sessionLockSheet: runtime.session.sessionLockSheet,
    workbookEditAttempted: true,
    finalPinnedWorkbookName: runtime.session.pinnedWorkbookName,
  };
}

/**
 * Parse model-emitted tool arguments for dispatch.
 *
 * @param value - JSON text emitted by the provider for one tool call.
 * @returns Parsed JSON value without imposing a tool-specific schema.
 * @throws An error when the provider emits malformed JSON.
 */
function parseToolArguments(value: string): unknown {
  try {
    return JSON.parse(value) as unknown;
  } catch {
    throw new Error("The model emitted invalid JSON tool arguments.");
  }
}

/**
 * Parse tool arguments for diagnostics without affecting execution.
 *
 * @param value - Potential JSON argument text.
 * @returns Parsed value, or `null` when parsing fails.
 */
function safeParseToolArguments(value: string): unknown {
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return null;
  }
}

/**
 * Build the non-model-facing metadata needed by the established taskpane tool
 * completion contract.
 *
 * Provider-side citations take precedence over legacy citations embedded in
 * the model-facing JSON. Research evidence is only attached for a successful
 * research call; observation metadata remains available for every tool.
 *
 * @param input - Completed native tool identity, detailed result, and duration.
 * @returns Metadata retained until the harness emits the matching completion.
 */
function buildNativeToolCompletionMetadata(input: {
  toolName: string;
  result: ToolCallDetailedResult;
  elapsedMs: number;
}): NativeToolCompletionMetadata {
  const isSuccessfulResearch =
    isAnyResearchToolName(input.toolName) && inferToolSuccess(input.result.output);
  const researchCitations = isSuccessfulResearch ? resolveResearchCitations(input.result) : [];
  const researchCitedValuesByUuid =
    researchCitations.length > 0
      ? extractCitedValuesById({
          ...parseObjectOutput(input.result.output),
          citations: researchCitations,
        })
      : {};

  return {
    elapsedMs: input.elapsedMs,
    ...(researchCitations.length > 0 ? { researchCitations } : {}),
    ...(Object.keys(researchCitedValuesByUuid).length > 0 ? { researchCitedValuesByUuid } : {}),
    ...(input.result.observationMetadata
      ? { observationMetadata: input.result.observationMetadata }
      : {}),
  };
}

/**
 * Resolve citations using the same side-channel-first compatibility policy as
 * the established plan-protocol runner.
 *
 * @param result - Detailed research tool result.
 * @returns Raw citations safe for UI/persistence handling, never model replay.
 */
function resolveResearchCitations(result: ToolCallDetailedResult): unknown[] {
  if (Array.isArray(result.researchCitations)) {
    return result.researchCitations;
  }
  const parsed = parseObjectOutput(result.output);
  return Array.isArray(parsed.citations) ? parsed.citations : [];
}

/**
 * Parse a tool output as an object without allowing malformed or array-shaped
 * payloads to affect native finalization.
 *
 * @param output - Model-facing tool output.
 * @returns Parsed object, or an empty object when unavailable.
 */
function parseObjectOutput(output: string): Record<string, unknown> {
  try {
    const parsed = JSON.parse(output) as unknown;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

/**
 * De-duplicate source cards while preserving first-seen research order.
 *
 * @param sources - Source cards accumulated from successful research calls.
 * @returns Stable de-duplicated source cards.
 */
function dedupeNativeSources(sources: readonly ExcelAgentSource[]): ExcelAgentSource[] {
  const seen = new Set<string>();
  return sources.filter((source) => {
    const key = [source.id, source.url ?? "", source.title, source.source].join("|");
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Map native harness lifecycle events onto the established taskpane event
 * contract.
 *
 * @param event - Provider-neutral harness event.
 * @param onEvent - Optional existing executor event sink.
 * @param completionMetadataByCallId - Detailed evidence held outside model
 *   replay until the matching completion event is emitted.
 * @returns Nothing.
 */
function forwardHarnessEvent(
  event: Parameters<NonNullable<NativeReactHarnessConfig["onEvent"]>>[0],
  onEvent?: (event: ExecutorRunEvent) => void,
  completionMetadataByCallId: Map<string, NativeToolCompletionMetadata> = new Map()
): void {
  if (!onEvent) return;
  switch (event.type) {
    case "iteration_start":
      onEvent(event);
      return;
    case "tool_call_started":
      onEvent({
        type: "tool_call_started",
        name: event.call.name,
        args: safeParseToolArguments(event.call.arguments),
        iteration: event.iteration,
        callId: event.call.callId,
      });
      return;
    case "tool_call_completed": {
      const metadata = completionMetadataByCallId.get(event.call.callId);
      completionMetadataByCallId.delete(event.call.callId);
      onEvent({
        type: "tool_call_completed",
        name: event.call.name,
        result: event.output,
        resultPreview: event.output.slice(0, 500),
        elapsedMs: metadata?.elapsedMs ?? 0,
        callId: event.call.callId,
        status: inferToolSuccess(event.output) ? "ok" : "error",
        iteration: event.iteration,
        ...(metadata?.researchCitations ? { researchCitations: metadata.researchCitations } : {}),
        ...(metadata?.researchCitedValuesByUuid
          ? {
              researchCitedValuesByUuid: metadata.researchCitedValuesByUuid,
            }
          : {}),
        ...(metadata?.observationMetadata
          ? { observationMetadata: metadata.observationMetadata }
          : {}),
      });
      return;
    }
    case "stopped":
      onEvent({
        type: "status",
        message: `Native executor stopped: ${event.reason}.`,
      });
  }
}

/**
 * Infer timeline status from a model-facing tool observation.
 *
 * Structured `ok: false` and `error` results are failures. Non-JSON output is
 * treated as successful because many established read tools return plain text.
 *
 * @param output - Bounded tool output replayed to the model.
 * @returns `true` when the observation does not explicitly report failure.
 */
function inferToolSuccess(output: string): boolean {
  try {
    const parsed = JSON.parse(output) as {
      ok?: unknown;
      error?: unknown;
    };
    return parsed.ok !== false && !parsed.error;
  } catch {
    return true;
  }
}
